import * as React from 'react';
import { initializeIcons } from '@fluentui/font-icons-mdl2';
import { Spinner } from '@fluentui/react-components';
import Cookies from 'js-cookie';
import { defineMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { useUpdateUserMutation, useUserQuery } from '@foundershub/api-client';
import { ErrorBoundary } from '../../components';
import { DrawerContext, DrawerProvider } from '../../components/FoundersHubUserOverlayDrawer';
import AccountRequiredRedirectLayout from '../../layouts/LoggedIn';
import LoggedOutLayout from '../../layouts/LoggedOut';
import LoggedOutSimpleLayout from '../../layouts/LoggedOutSimple';
import { useCookieConsent } from '../../modules/cookie-consent';
import { useUpdateLocale } from '../../modules/intl';
import type { Locale } from '../../modules/intl/locales';
import { getLocaleHtml } from '../../modules/intl/locales';
import {
  MFS_BLOCK_AZURE_ACTIVATION_FLAG,
  MFS_REPLATFORM_FLAG,
  MFS_REPLATFORM_URL,
  useMfsReplatformLoading,
} from '../../modules/mfs-replatform';
import { MigrationCtaModal } from '../../modules/mfs-replatform/MigrationCtaModal';
import { NoAzureActivationModal } from '../../modules/mfs-replatform/NoAzureActivationModal';
import redirect from '../../modules/redirect';
import { setUser, unsetUser } from '../../modules/telemetry';
import SupportDrawer from '../../pages/SupportForm/SupportDrawer';
import {
  loggedInRedirectsWithFlag,
  loggedInRedirectsWithoutFlag,
  loggedInRoutesWithFlag,
  loggedInRoutesWithoutFlag,
  loggedOutRedirectsWithFlag,
  loggedOutRoutesWithFlag,
  NotFoundLoggedIn,
  NotFoundLoggedOut,
  Routes,
} from '../../routes';
import { useFeatureFlagEnabled } from '../../store';
import { useAccount } from '../../store/slices/account';
import { bootstrap } from '..';

initializeIcons();

type MigrationStatus = {
  eligible: boolean;
  hasProgramOverviewAccess: boolean;
  msaEmail: string | null;
  canContinueToAzure: boolean;
  hasActivatedAzure: boolean;
  activationContactEmail: string | null;
};

export function App() {
  const { data: user, isLoading, error } = useUserQuery();
  const selectedAccount = useAccount();
  const account = user?.accounts.find((candidate) => candidate.id === selectedAccount?.id) ?? user?.accounts[0];
  const history = useHistory();
  const [userLocaleFromQueryParam] = React.useState(history.location.search.includes('lang='));

  // remove after 06/30/2025 deploy
  const buildWithAIAndDiscover = useFeatureFlagEnabled('build-with-ai-and-discover');

  const loggedOutReds = loggedOutRedirectsWithFlag;
  const loggedOutRts = loggedOutRoutesWithFlag;

  let loggedInReds = loggedInRedirectsWithoutFlag;
  let loggedInRts = loggedInRoutesWithoutFlag;
  if (buildWithAIAndDiscover) {
    loggedInReds = loggedInRedirectsWithFlag;
    loggedInRts = loggedInRoutesWithFlag;
  }

  const location = useLocation();
  const setUserLocale = useUpdateLocale();
  const { siteConsent } = useCookieConsent();
  // Added for 1DS tracking (Only capture on initial load)
  const [mcId] = React.useState<string | null>(new URLSearchParams(window.location.search).get('WT.mc_id'));
  const intl = useIntl();
  let redirects = loggedOutReds;
  let routes = loggedOutRts;
  const notFound = user ? NotFoundLoggedIn : NotFoundLoggedOut;
  let fallback = '/login';
  let antiRoutes = loggedInRts;
  const [updateUserDetail] = useUpdateUserMutation();
  let routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
  let parentLayout = <LoggedOutLayout>{routesComponent}</LoggedOutLayout>;

  const isSignupPath = history.location.pathname.includes('/signup');
  const isApplyPath = history.location.pathname.startsWith('/apply');
  const isApplySubmittedPath = history.location.pathname === '/apply/submitted';
  const isLogoutPath = history.location.pathname.includes('/logout');
  const isReferralPath = history.location.pathname.includes('/referral');
  const isRegisterPath = history.location.pathname.includes('/register');
  const isLoginPath = history.location.pathname === '/login';
  const isPortalEntryPath = !isApplySubmittedPath && (isSignupPath || isApplyPath || isReferralPath || isRegisterPath);
  const userHasAccounts = !!user && user.accounts.length > 0;

  const mfsReplatformEnabled = useFeatureFlagEnabled(MFS_REPLATFORM_FLAG);
  const blockUnactivatedAzureInFh = useFeatureFlagEnabled(MFS_BLOCK_AZURE_ACTIVATION_FLAG);
  const mfsConfigLoading = useMfsReplatformLoading();
  const [migrationStatus, setMigrationStatus] = React.useState<MigrationStatus | null>(null);

  // Show spinner on paths that may be affected while MFS config loads.
  // Not gated by mfsReplatformEnabled — we need to hold the render until the flag value is known.
  const isMfsAffectedPath =
    isLoginPath || isPortalEntryPath || isApplySubmittedPath || (!user && history.location.pathname === '/');
  // Only redirect to portal once we know the flag is on and user loading has settled.
  const shouldMfsRedirect =
    mfsReplatformEnabled && !isLoading && (isPortalEntryPath || (isApplySubmittedPath && !user));

  React.useEffect(() => {
    if (shouldMfsRedirect) {
      window.location.href = MFS_REPLATFORM_URL;
    }
  }, [shouldMfsRedirect]);

  React.useEffect(() => {
    let mounted = true;

    if (!user || !account || shouldMfsRedirect || isLogoutPath) {
      setMigrationStatus(null);
      return () => {
        mounted = false;
      };
    }

    const fallbackMigrationStatus: MigrationStatus = {
      eligible: false,
      hasProgramOverviewAccess: false,
      msaEmail: null,
      canContinueToAzure: false,
      hasActivatedAzure: Boolean((account.azure_user ?? '').trim()),
      activationContactEmail: null,
    };

    setMigrationStatus(null);

    void fetch(`/auth/migration/status?accountId=${encodeURIComponent(account.id)}`, {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then(async (resp) => {
        if (!resp.ok) {
          return null;
        }
        return (await resp.json()) as Partial<MigrationStatus>;
      })
      .then((body) => {
        if (mounted) {
          setMigrationStatus({
            eligible: Boolean(body?.eligible),
            hasProgramOverviewAccess: Boolean(body?.hasProgramOverviewAccess),
            msaEmail: (body?.msaEmail ?? '').trim() || null,
            canContinueToAzure: Boolean(body?.canContinueToAzure),
            hasActivatedAzure:
              typeof body?.hasActivatedAzure === 'boolean'
                ? body.hasActivatedAzure
                : fallbackMigrationStatus.hasActivatedAzure,
            activationContactEmail: (body?.activationContactEmail ?? '').trim() || null,
          });
        }
      })
      .catch(() => {
        if (mounted) {
          setMigrationStatus(fallbackMigrationStatus);
        }
      });

    return () => {
      mounted = false;
    };
  }, [user, account, shouldMfsRedirect, isLogoutPath]);

  if (user && !isApplyPath && !isLogoutPath) {
    fallback = '/benefits';
    redirects = loggedInReds;
    routes = loggedInRts;
    antiRoutes = loggedOutRts;
    routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
    parentLayout = <AccountRequiredRedirectLayout>{routesComponent}</AccountRequiredRedirectLayout>;
  } else if (
    user &&
    (isApplyPath || isLogoutPath) &&
    (!mfsReplatformEnabled || (!isPortalEntryPath && !isApplySubmittedPath))
  ) {
    redirects = loggedInReds;
    routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
    parentLayout = <LoggedOutLayout>{routesComponent}</LoggedOutLayout>;
  } else if (mfsReplatformEnabled && user && isApplySubmittedPath) {
    redirects = [];
    routes = loggedOutRts;
    antiRoutes = [];
    routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
    parentLayout = <LoggedOutLayout>{routesComponent}</LoggedOutLayout>;
  } else if (mfsReplatformEnabled && user && userHasAccounts && isPortalEntryPath) {
    fallback = '/benefits';
    redirects = [
      { from: '/signup', to: '/' },
      { from: '/apply', to: '/' },
      { from: '/referral', to: '/' },
      { from: '/register', to: '/' },
      ...loggedInReds,
    ];
    routes = loggedInRts;
    antiRoutes = loggedOutRts;
    routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
    parentLayout = <AccountRequiredRedirectLayout>{routesComponent}</AccountRequiredRedirectLayout>;
  } else if (mfsReplatformEnabled && user && !userHasAccounts && (isApplyPath || isApplySubmittedPath)) {
    redirects = [];
    routes = loggedOutRts;
    antiRoutes = [];
    routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
    parentLayout = <LoggedOutLayout>{routesComponent}</LoggedOutLayout>;
  } else if (mfsReplatformEnabled && !user) {
    redirects = [
      { from: '/', to: '/login' },
      { from: '/logout', to: '/login' },
    ];
    routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
    parentLayout = <LoggedOutLayout>{routesComponent}</LoggedOutLayout>;
  }

  if (!user && (isSignupPath || isReferralPath)) {
    parentLayout = <LoggedOutSimpleLayout>{routesComponent}</LoggedOutSimpleLayout>;
  }

  React.useEffect(() => {
    if (user) {
      setUser(user);
    }
    if (error) {
      unsetUser();
    }
  }, [user && user.email, error]);

  React.useEffect(() => {
    const urlParams = new URLSearchParams(location.search);

    if (user && user.locale) {
      setUserLocale(user.locale as Locale);
    }
    if (urlParams.has('lang')) {
      const lang = getLocaleHtml(urlParams.get('lang') as Locale);
      setUserLocale(lang);
    }
    // n.b. intentionally not depending on location.search to preserve ?lang= across all routes in a session
  }, [userLocaleFromQueryParam, user && user.locale]);

  React.useEffect(() => {
    if (user) {
      const msfpc_data = Cookies.get('MSFPC');

      // MSFPC GUID has no dashes
      const msfpc_regex = /GUID=([a-z0-9]{32})/;
      const msfpc_id = !!msfpc_data && msfpc_data.match(msfpc_regex)?.at(1);
      if (user.msfpc_id == null && msfpc_id) {
        updateUserDetail({ msfpc_id });
      }
    }
  }, [user]);

  React.useEffect(() => {
    // Added for 1DS tracking
    if (siteConsent) {
      if (!siteConsent.isConsentRequired || siteConsent.getConsent().Analytics) {
        if (mcId !== null && location.pathname !== '/apply/submitted') {
          Cookies.set('mc_id', mcId, { expires: 365 });
        }
      } else {
        Cookies.remove('mc_id');
      }
    }
  }, [siteConsent?.getConsentFor('Analytics')]);

  const parameterizedRedirect = redirect(history.location.pathname, antiRoutes, redirects, fallback);

  if (parameterizedRedirect != null) {
    redirects.push(parameterizedRedirect);
  }

  const isSettingsPath = history.location.pathname.startsWith('/settings');
  const showMigrationCtaModal = Boolean(migrationStatus?.eligible && !isSettingsPath);
  const showNoAzureActivationModal =
    !showMigrationCtaModal &&
    Boolean(
      user &&
        migrationStatus &&
        mfsReplatformEnabled &&
        blockUnactivatedAzureInFh &&
        !migrationStatus.hasActivatedAzure &&
        !isSettingsPath &&
        !isLogoutPath
    );

  return (
    <ErrorBoundary type="full">
      <DrawerProvider>
        {isLoading && !error && !user ? (
          <Spinner label={intl.formatMessage(loadingLabel)} labelPosition="below" />
        ) : (mfsConfigLoading && isMfsAffectedPath) || shouldMfsRedirect ? (
          <Spinner label={intl.formatMessage(loadingLabel)} labelPosition="below" />
        ) : (
          <div>
            {parentLayout}
            <MigrationModalLayer
              migrationStatus={migrationStatus}
              showMigrationCtaModal={showMigrationCtaModal}
              showNoAzureActivationModal={showNoAzureActivationModal}
            />
          </div>
        )}
      </DrawerProvider>
    </ErrorBoundary>
  );
}

function MigrationModalLayer({
  migrationStatus,
  showMigrationCtaModal,
  showNoAzureActivationModal,
}: {
  migrationStatus: MigrationStatus | null;
  showMigrationCtaModal: boolean;
  showNoAzureActivationModal: boolean;
}) {
  const { setChildren, setShowDrawer } = React.useContext(DrawerContext);

  function openSupportDrawer() {
    setChildren(<SupportDrawer />);
    setShowDrawer(true);
  }

  function continueToAzure() {
    void fetch('/auth/migration/continue', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'x-csrftoken': Cookies.get('csrftoken') ?? '' },
    })
      .then(async (resp) => {
        if (resp.status === 204 || !resp.ok) {
          return null;
        }
        return (await resp.json()) as { redirectUrl?: string };
      })
      .then((body) => {
        if (body?.redirectUrl) {
          window.location.href = body.redirectUrl;
        }
      });
  }

  if (!migrationStatus) {
    return null;
  }

  if (showMigrationCtaModal) {
    return (
      <MigrationCtaModal
        hasProgramOverviewAccess={migrationStatus.hasProgramOverviewAccess}
        msaEmail={migrationStatus.msaEmail}
        canContinueToAzure={migrationStatus.canContinueToAzure}
        onContinueToAzure={continueToAzure}
        onContactSupport={openSupportDrawer}
      />
    );
  }

  if (showNoAzureActivationModal) {
    return (
      <NoAzureActivationModal
        contactEmail={migrationStatus.activationContactEmail}
        onGoToAzurePortal={() => {
          window.location.href = MFS_REPLATFORM_URL;
        }}
        onContactSupport={openSupportDrawer}
      />
    );
  }

  return null;
}

const loadingLabel = defineMessage({ defaultMessage: 'Loading user info', description: 'Label for loading spinner' });

bootstrap(App);
